import { designArtStatuses } from 'constants/statuses';

/**
 * Parse campaign art from GraphQL response
 * Transforms GraphQL CampaignArt structure to frontend format
 */
export const parseCampaignArtFromGraphQL = (campaignArt) => {
  if (!campaignArt) {
    return {
      designArts: [],
      artPreferences: {}
    };
  }

  const {
    id,
    campaignArtBrief,
    campaignArtFiles,
    createDate,
    installed,
    installedDate
  } = campaignArt;

  // Filter art files: those without artProofStatusId are reference/upload files (client uploads)
  // Those with artProofStatusId are art proofs (designer files)
  const referenceFiles = campaignArtFiles 
    ? campaignArtFiles.filter(file => !file.artProofStatusId)
    : [];

  // Parse reference files to images format
  const referenceImages = referenceFiles.map((file) => ({
    fileId: file.id,
    fileName: file.fileUrl?.split('/').pop() || 'reference-file',
    fileDisplayName: file.fileUrl?.split('/').pop() || 'reference-file',
    fileUrl: file.fileUrl
  }));

  // Parse concept URLs: stored as delimited string "url1 || url2 || url3"
  const conceptUrls = campaignArtBrief?.conceptFileUrl 
    ? campaignArtBrief.conceptFileUrl
        .split('||')
        .map(url => url.trim())
        .filter(url => url)
        .map(url => ({ id: url }))
    : [];

  const headlineText = campaignArtBrief?.headlineText || '';
  
  // Check if text contains request type prefix (has period in first 100 chars)
  const hasRequestType = headlineText.includes('.') && headlineText.indexOf('.') < 100;
  
  // If no request type prefix, add default one for compatibility
  const textWithPrefix = hasRequestType 
    ? headlineText 
    : `Request to Match Template Selected. ${headlineText}`;

  const parseContactField = (value) => {
    if (!value) return { value: '', position: 'Top' };
    
    // Check for position suffix
    if (value.includes(' - Top & Bottom')) {
      return {
        value: value.replace(' - Top & Bottom', '').trim(),
        position: 'Top & Bottom'
      };
    } else if (value.includes(' - Bottom')) {
      return {
        value: value.replace(' - Bottom', '').trim(),
        position: 'Bottom'
      };
    } else if (value.includes(' - Top')) {
      return {
        value: value.replace(' - Top', '').trim(),
        position: 'Top'
      };
    }
    
    // No position suffix - default to Top
    return { value: value.trim(), position: 'Top' };
  };

  // Parse contact fields
  const phoneData = parseContactField(campaignArtBrief?.phone);
  const emailData = parseContactField(campaignArtBrief?.email);
  const websiteData = parseContactField(campaignArtBrief?.website);
  const facebookData = parseContactField(campaignArtBrief?.facebook);
  const instagramData = parseContactField(campaignArtBrief?.instagram);
  const twitterData = parseContactField(campaignArtBrief?.twitter);
  const tiktokData = parseContactField(campaignArtBrief?.tikTok);
  const snapchatData = parseContactField(campaignArtBrief?.snapchat);

  // Parse art preferences (from CampaignArtBrief)
  const artPreferences = campaignArtBrief ? {
    id: campaignArtBrief.id,
    campaignId: campaignArtBrief.campaignId,
    text: textWithPrefix, // Text with request type prefix for backwards compatibility
    colors: campaignArtBrief.colors || '',
    concept: conceptUrls, // Array of { id: 'url' } objects
    phone: phoneData.value,
    email: emailData.value,
    brand: '', // Not in new schema
    website: websiteData.value,
    facebook: facebookData.value,
    instagram: instagramData.value,
    twitter: twitterData.value,
    tiktok: tiktokData.value,
    snapchat: snapchatData.value,
    // Store positions separately for edit form
    phonePosition: phoneData.position,
    emailPosition: emailData.position,
    websitePosition: websiteData.position,
    facebookPosition: facebookData.position,
    instagramPosition: instagramData.position,
    twitterPosition: twitterData.position,
    tiktokPosition: tiktokData.position,
    snapchatPosition: snapchatData.position,
    images: referenceImages, // Reference files uploaded by customer
    status: 'submitted',
    type: 'client',
    createdDate: campaignArtBrief.createDate
  } : {};

  // Only include files with artProofStatusId (actual art proofs from designer)
  // Hide drafts until sent to customer (same as Manage Portal View Art Design)
  const artProofFiles = campaignArtFiles 
    ? campaignArtFiles.filter(file => file.artProofStatusId && !file.isDraft)
    : [];

  // Sort by creation date (newest first) on frontend since backend doesn't support order on navigation property
  const sortedArtProofs = [...artProofFiles].sort((a, b) => 
    new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
  );

  const designArts = sortedArtProofs.map((file) => {
    // Public top-level notes only (exclude internal notes and replies)
    const publicTopLevelNotes = (file.campaignArtNotes || []).filter(
      (note) => !note.internalNote && note.parentNoteId == null
    );

    // Sort notes by creation date (oldest first to find the caption)
    const sortedNotesOldest = [...publicTopLevelNotes].sort((a, b) =>
      new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
    );

    // First note (oldest) is the file caption (text)
    const firstNote = sortedNotesOldest[0];
    const remainingNotes = sortedNotesOldest.slice(1);

    const sortedComments = remainingNotes;

    const comments = sortedComments.map((note) => {
      const isClient = note.senderTypeId === 160;
      const isTerraboost = note.senderTypeId === 161;

      return {
        id: note.id,
        message: note.note,
        createdDate: note.createDate,
        username: isClient ? 'You' : note.senderType?.typeName || 'User',
        // Avatar props for differentiation
        avatarColor: isClient ? 'primary' : isTerraboost ? 'secondary' : 'grey',
        avatarLetter: isClient ? 'Y' : isTerraboost ? 'T' : 'U',
        isMy: false // All comments align left
      };
    });

    const statusName = file.artProofStatus?.statusName;
    const statusId = file.artProofStatusId;

    const isAwaitingReview = !statusName || statusName.toLowerCase() === 'example';
    
    return {
      id: file.id,
      campaignId: campaignArt.campaignId,
      fileUrl: file.fileUrl,
      thumbnailUrl: file.thumbnailUrl || file.fileUrl,
      printReady: file.printReady || false,
      status: isAwaitingReview ? null : statusName.toLowerCase(), // null for "Example" = show buttons
      statusId: statusId, // Art proof status ID
      createdDate: file.createDate,
      // First note is the caption/description, or default text
      text: firstNote?.note || (statusName 
        ? `Art Proof - ${statusName}` 
        : 'Art Proof'),
      comments: comments,
      images: [{
        fileId: file.id,
        fileName: file.fileUrl?.split('/').pop() || 'art-file',
        fileDisplayName: file.fileUrl?.split('/').pop() || 'art-file',
        fileUrl: file.fileUrl
      }],
      type: 'proof',
      name: 'Art Designer'
    };
  });

  return {
    designArts,
    artPreferences
  };
};

/**
 * Parse campaign art files from GraphQL response
 */
export const parseCampaignArtFilesFromGraphQL = (artFiles) => {
  if (!Array.isArray(artFiles)) return [];

  // Sort files by creation date (newest first) on frontend
  const sortedFiles = [...artFiles].sort((a, b) => 
    new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
  );

  return sortedFiles.map((file) => {
    // Public top-level notes only (exclude internal notes and replies)
    const publicTopLevelNotes = (file.campaignArtNotes || []).filter(
      (note) => !note.internalNote && note.parentNoteId == null
    );

    // Sort notes by creation date (oldest first) on frontend
    const sortedNotes = [...publicTopLevelNotes].sort((a, b) =>
      new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
    );

    return {
      id: file.id,
      fileUrl: file.fileUrl,
      thumbnailUrl: file.thumbnailUrl || file.fileUrl,
      printReady: file.printReady || false,
      status: file.artProofStatus?.statusName || 'pending',
      statusId: file.artProofStatusId,
      createdDate: file.createDate,
      comments: sortedNotes.map((note) => {
        const isClient = note.senderTypeId === 160;
        const isTerraboost = note.senderTypeId === 161;

        return {
          id: note.id,
          message: note.note,
          createdDate: note.createDate,
          username: note.senderType?.typeName || 'User',
          avatarColor: isClient ? 'primary' : isTerraboost ? 'secondary' : 'grey',
          avatarLetter: isClient ? 'C' : isTerraboost ? 'T' : 'U',
          isMy: false
        };
      })
    };
  });
};

/**
 * Transform frontend art brief data to GraphQL input (for create)
 */
export const transformToArtBriefInput = (formData, campaignId) => {
  let conceptUrl = null;
  if (formData.concept) {
    if (Array.isArray(formData.concept) && formData.concept.length > 0) {
      const urls = formData.concept.map(item => {
        if (typeof item === 'object' && item.id) {
          return item.id;
        }
        return item;
      }).filter(url => url);
      
      conceptUrl = urls.join(' || ');
    } else if (typeof formData.concept === 'string') {
      conceptUrl = formData.concept;
    }
  }

  let fileUrls = [];
  if (formData.uploads && Array.isArray(formData.uploads)) {
    fileUrls = formData.uploads.map(item => {
      if (typeof item === 'string') {
        return item;
      } else if (typeof item === 'object' && item.fileUrl) {
        return item.fileUrl;
      }
      return null;
    }).filter(url => url);
  }

  return {
    campaignId,
    fileUrls: fileUrls,
    headlineText: formData.text || null,
    colors: formData.colors || null,
    phone: formData.phone || null,
    email: formData.email || null,
    website: formData.website || null,
    facebook: formData.facebook || null,
    instagram: formData.instagram || null,
    twitter: formData.twitter || null,
    tiktok: formData.tiktok || null,
    snapchat: formData.snapchat || null,
    conceptFileUrl: conceptUrl
  };
};

/**
 * Transform frontend art brief data to GraphQL update input
 */
export const transformToUpdateArtBriefInput = (formData, campaignId, artBriefId) => {
  let conceptUrl = null;
  if (formData.concept) {
    if (Array.isArray(formData.concept) && formData.concept.length > 0) {
      const urls = formData.concept.map(item => {
        if (typeof item === 'object' && item.id) {
          return item.id;
        }
        return item;
      }).filter(url => url);
      
      conceptUrl = urls.join(' || ');
    } else if (typeof formData.concept === 'string') {
      conceptUrl = formData.concept;
    }
  }

  let fileUrls = [];
  if (formData.uploads && Array.isArray(formData.uploads)) {
    fileUrls = formData.uploads.map(item => {
      if (typeof item === 'string') {
        return item;
      } else if (typeof item === 'object' && item.fileUrl) {
        return item.fileUrl;
      }
      return null;
    }).filter(url => url);
  }

  return {
    campaignArtBriefId: artBriefId,
    campaignId,
    fileUrls: fileUrls,
    headlineText: formData.text || null,
    colors: formData.colors || null,
    phone: formData.phone || null,
    email: formData.email || null,
    website: formData.website || null,
    facebook: formData.facebook || null,
    instagram: formData.instagram || null,
    twitter: formData.twitter || null,
    tiktok: formData.tiktok || null,
    snapchat: formData.snapchat || null,
    conceptFileUrl: conceptUrl
  };
};

